import { watchDeployedBuild, browserBuildDeps } from '@shared/lib';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Keeps a tab on the deployed build (`shared/lib/deployedBuild`). It sits inside the Router
 * because a route change is one of the safe moments to reload, and only the Router's history
 * hears one; a mutation in flight (a save, an upload) holds the reload back.
 *
 * A route change is a new PATH. A page that keeps its state in the query string (the
 * data-analysis view, drill and panel tab, a documents search) writes it while the user
 * works on that page, which is no moment to reload it.
 */
export function DeployedBuildReload(): null {
  const history = useHistory();
  const queryClient = useQueryClient();

  useEffect(
    () =>
      watchDeployedBuild({
        ...browserBuildDeps(),
        isBusy: () => queryClient.isMutating() > 0,
        onNavigate: (listener) => {
          let pathname = history.location.pathname;
          return history.listen((location) => {
            if (location.pathname === pathname) return;
            pathname = location.pathname;
            listener();
          });
        },
      }),
    [history, queryClient],
  );

  return null;
}
